import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

// Simple math captcha – zwei Zahlen zwischen 1 und 9
const generateCaptcha = () => {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { a, b, answer: a + b };
};

export default function KontaktSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
    website: '', // 🍯 Honeypot – muss LEER bleiben
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Anti-Spam: Zeitstempel + Math Captcha
  const [formOpenedAt] = useState(Date.now());
  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 1. Honeypot prüfen – wenn ausgefüllt = Bot
    if (formData.website && formData.website.trim() !== '') {
      // Silent success (Bot merkt nichts)
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 6000);
      return;
    }

    // 2. Zeit-Check – Formular <3 s = Bot
    const elapsed = Date.now() - formOpenedAt;
    if (elapsed < 3000) {
      setError('Bitte nehmen Sie sich einen Moment Zeit für Ihre Anfrage.');
      return;
    }

    // 3. Math Captcha prüfen
    if (parseInt(captchaAnswer, 10) !== captcha.answer) {
      setError('Bitte lösen Sie die Sicherheitsfrage korrekt.');
      setCaptcha(generateCaptcha());
      setCaptchaAnswer('');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          website: formData.website, // Honeypot mitschicken
          form_time_ms: elapsed,     // Zeit-Check für Backend
        }),
      });

      if (!response.ok) {
        throw new Error('Senden fehlgeschlagen');
      }

      setSubmitted(true);
      setFormData({ name: '', phone: '', email: '', subject: '', message: '', website: '' });
      setCaptcha(generateCaptcha());
      setCaptchaAnswer('');
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      setError('Ihre Anfrage konnte leider nicht gesendet werden. Bitte versuchen Sie es erneut oder rufen Sie uns an.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="kontakt-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
            Kontakt
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#1A3A22] tracking-tight mt-3 mb-4">
            Elektriker in Bonn <span className="text-[#2D5A27]">kontaktieren</span>
          </h2>
          <p className="font-['Manrope'] text-gray-600 text-base sm:text-lg max-w-xl mx-auto">
            Stellen Sie jetzt Ihre unverbindliche Anfrage – wir melden uns zeitnah bei Ihnen. Kostenlose Erstberatung für Ihr Elektroprojekt.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 space-y-8"
          >
            <div>
              <h3 className="font-['Outfit'] text-2xl font-bold text-[#1A3A22] mb-6">
                Kontaktdaten
              </h3>
              <div className="space-y-5">
                <a href="tel:+4922823 4644" className="flex items-start gap-4 group" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-[#F4F8F4] flex items-center justify-center flex-shrink-0 group-hover:bg-[#2D5A27] transition-colors duration-300">
                    <Phone className="w-5 h-5 text-[#2D5A27] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="font-['Manrope'] text-gray-500 text-xs block uppercase tracking-wider">Telefon</span>
                    <span className="font-['Manrope'] text-[#1A3A22] font-medium">+49 228 23 46 44</span>
                    <span className="font-['Manrope'] text-gray-500 text-sm block">Mobil: +49 152 37098300</span>
                  </div>
                </a>

                <a href="mailto:info@daniels-gmbh.net" className="flex items-start gap-4 group" data-testid="contact-email">
                  <div className="w-12 h-12 bg-[#F4F8F4] flex items-center justify-center flex-shrink-0 group-hover:bg-[#2D5A27] transition-colors duration-300">
                    <Mail className="w-5 h-5 text-[#2D5A27] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="font-['Manrope'] text-gray-500 text-xs block uppercase tracking-wider">E-Mail</span>
                    <span className="font-['Manrope'] text-[#1A3A22] font-medium">info@daniels-gmbh.net</span>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="w-12 h-12 bg-[#F4F8F4] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#2D5A27]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="font-['Manrope'] text-gray-500 text-xs block uppercase tracking-wider">Adresse</span>
                    <span className="font-['Manrope'] text-[#1A3A22] font-medium block">Burbacher Straße 177</span>
                    <span className="font-['Manrope'] text-[#1A3A22] font-medium">53129 Bonn</span>
                  </div>
                </div>

                <a
                  href="https://www.google.com/maps/search/?api=1&query=Burbacher+Stra%C3%9Fe+177+53129+Bonn"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-maps-button"
                  className="flex items-center gap-3 mt-2 bg-[#F4F8F4] hover:bg-[#2D5A27] px-5 py-3 group transition-all duration-300"
                >
                  <ExternalLink className="w-5 h-5 text-[#2D5A27] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  <span className="font-['Manrope'] text-[#1A3A22] group-hover:text-white text-sm font-medium transition-colors">
                    Standort in Google Maps anzeigen
                  </span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-3"
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-['Manrope'] text-sm font-medium text-[#1A3A22] block mb-2">
                    Name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ihr Name"
                    required
                    data-testid="contact-name-input"
                    className="h-12 border-gray-200 focus:border-[#2D5A27] focus:ring-[#2D5A27]/20 rounded-none font-['Manrope']"
                  />
                </div>
                <div>
                  <label className="font-['Manrope'] text-sm font-medium text-[#1A3A22] block mb-2">
                    Telefonnummer *
                  </label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Ihre Telefonnummer"
                    required
                    data-testid="contact-phone-input"
                    className="h-12 border-gray-200 focus:border-[#2D5A27] focus:ring-[#2D5A27]/20 rounded-none font-['Manrope']"
                  />
                </div>
              </div>
              <div>
                <label className="font-['Manrope'] text-sm font-medium text-[#1A3A22] block mb-2">
                  E-Mail *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Ihre E-Mail-Adresse"
                  required
                  data-testid="contact-email-input"
                  className="h-12 border-gray-200 focus:border-[#2D5A27] focus:ring-[#2D5A27]/20 rounded-none font-['Manrope']"
                />
              </div>
              <div>
                <label className="font-['Manrope'] text-sm font-medium text-[#1A3A22] block mb-2">
                  Betreff
                </label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="z.B. Altbausanierung, Neubau, Beratung..."
                  data-testid="contact-subject-input"
                  className="h-12 border-gray-200 focus:border-[#2D5A27] focus:ring-[#2D5A27]/20 rounded-none font-['Manrope']"
                />
              </div>
              <div>
                <label className="font-['Manrope'] text-sm font-medium text-[#1A3A22] block mb-2">
                  Nachricht
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                  rows={5}
                  data-testid="contact-message-input"
                  className="border-gray-200 focus:border-[#2D5A27] focus:ring-[#2D5A27]/20 rounded-none font-['Manrope'] resize-none"
                />
              </div>
              {/* 🍯 Honeypot – nur für Bots sichtbar (a11y: aria-hidden) */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  top: 'auto',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                <label htmlFor="website">Bitte nicht ausfüllen</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Datenschutz Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacy-consent"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  required
                  data-testid="contact-privacy-checkbox"
                  className="mt-1 w-4 h-4 text-[#2D5A27] border-gray-300 rounded focus:ring-[#2D5A27]"
                />
                <label htmlFor="privacy-consent" className="font-['Manrope'] text-sm text-gray-600 leading-relaxed">
                  Ich habe die Datenschutzerklärung gelesen und bin mit der Verarbeitung meiner Daten
                  zur Bearbeitung meiner Anfrage einverstanden. *
                </label>
              </div>

              {/* Anti-Spam Rechenaufgabe */}
              <div className="bg-[#F4F8F4] border border-[#2D5A27]/20 p-4">
                <label htmlFor="captcha-input" className="font-['Manrope'] text-sm font-medium text-[#1A3A22] block mb-2">
                  Sicherheitsfrage: Wie viel ist <strong>{captcha.a} + {captcha.b}</strong>? *
                </label>
                <Input
                  id="captcha-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Ihre Antwort"
                  required
                  data-testid="contact-captcha-input"
                  className="h-11 max-w-[200px] border-gray-200 focus:border-[#2D5A27] focus:ring-[#2D5A27]/20 rounded-none font-['Manrope']"
                />
                <p className="font-['Manrope'] text-xs text-gray-500 mt-2">
                  Diese kurze Rechenaufgabe schützt uns vor automatisierten Spam-Nachrichten.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !privacyAccepted}
                data-testid="contact-submit-button"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#2D5A27] hover:bg-[#10B981] disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-10 py-4 font-['Outfit'] font-semibold text-base tracking-[0.1em] uppercase transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Wird gesendet...' : 'Anfrage absenden'}
              </button>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-success-message"
                  className="bg-[#F4F8F4] border border-[#2D5A27]/20 p-4 mt-4"
                >
                  <p className="font-['Manrope'] text-[#2D5A27] font-medium text-sm">
                    Vielen Dank für Ihre Anfrage! Wir melden uns schnellstmöglich bei Ihnen.
                  </p>
                </motion.div>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-error-message"
                  className="bg-red-50 border border-red-200 p-4 mt-4"
                >
                  <p className="font-['Manrope'] text-red-700 font-medium text-sm">
                    {error}
                  </p>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
